

// src/pages/SingleListingGenerator/components/AsinLookupForm.js
import React, { useState, useContext, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { FaTimes } from 'react-icons/fa';
import styles from '../SingleListingGeneratorPage.module.css';

import AuthContext from '../../../context/AuthContext';
import { API_BASE_URL } from '../constants';
import AsinNotFoundModal from '../../../components/ListingScore/components/AsinNotFoundModal';
import SelectProductCatalogueModal from '../../../components/UI/SelectProductCatalogueModal';
import { useAmazonStatus } from '../../../hooks/useAmazonStatus';
import { useAmazonSync } from '../../../context/AmazonSyncContext';

const AsinLookupForm = forwardRef(({ onDataFetched, disabled, resetTrigger, initialAsin = '', disableAutoFetch = false, onLoadingChange, onError, onAsinChange }, ref) => {
  const [asin, setAsin] = useState(initialAsin);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchedProductData, setFetchedProductData] = useState(null);

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);
  const [error, setError] = useState(null);
  const [hasAutoFetched, setHasAutoFetched] = useState(false);
  const [showAsinNotFoundModal, setShowAsinNotFoundModal] = useState(false);
  const authCtx = useContext(AuthContext);
  const { isBridgeActive } = useAmazonSync();

  // Catalogue modal state
  const [showCatalogueModal, setShowCatalogueModal] = useState(false);
  const [catalogueProducts, setCatalogueProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedAsins, setSelectedAsins] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Amazon connection status
  const { connected: amazonConnected } = useAmazonStatus(authCtx?.token);

  // Reset ASIN form when resetTrigger changes
  useEffect(() => {
    setAsin('');
    setError(null);
    setHasAutoFetched(false);
    setFetchedProductData(null);
    if (onAsinChange) {
      onAsinChange('');
    }
  }, [resetTrigger, onAsinChange]);

  useEffect(() => {
    if (initialAsin && !isLoading && !hasAutoFetched) {
      setAsin(initialAsin);
      setHasAutoFetched(true);
      if (!disableAutoFetch) {
        handleFetchFromCatalogue(initialAsin);
      }
    }
  }, [initialAsin, disableAutoFetch, isLoading, hasAutoFetched]);

  // Expose fetch function, loading state, and catalogue modal to parent via ref
  useImperativeHandle(ref, () => ({
    fetchProduct: (e) => { if (asin.trim()) handleFetchFromCatalogue(asin.trim()); },
    isLoading: isLoading,
    asin: asin,
    openCatalogueModal: handleOpenCatalogueModal,
    loadingProducts: loadingProducts,
    amazonConnected: amazonConnected,
  }));

  // Fetch catalogue products list for modal
  const fetchCatalogueProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const token = authCtx?.token;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_BASE_URL}/amazon/catalogue`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) throw new Error('Failed to fetch catalogue');

      const data = await response.json();
      setCatalogueProducts(data.products || []);
    } catch (err) {
      setError('Failed to load product catalogue. Please try again.');
    } finally {
      setLoadingProducts(false);
    }
  }, [authCtx?.token]);

  // Open catalogue modal
  const handleOpenCatalogueModal = () => {
    setSelectedAsins([]);
    setSelectAll(false);
    fetchCatalogueProducts();
    setShowCatalogueModal(true);
  };

  // Handle ASIN selection in modal
  const handleAsinSelect = (selectedAsin) => {
    // Single select only for single listing
    setSelectedAsins([selectedAsin]);
    setSelectAll(false);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedAsins([]);
    } else {
      setSelectedAsins(catalogueProducts.map(p => p.asin));
    }
    setSelectAll(!selectAll);
  };

  // Fetch product — open accounts use Rainforest, close accounts use catalogue
  const handleFetchFromCatalogue = async (asinToFetch) => {
    if (!asinToFetch) return;

    setIsLoading(true);
    setError(null);

    // Read account_type fresh — prefer context, fallback to localStorage
    let accountType = authCtx?.user?.account_type;
    if (!accountType) {
      try {
        const stored = JSON.parse(localStorage.getItem('authUser'));
        accountType = stored?.account_type || 'close';
      } catch { accountType = 'close'; }
    }
    const isOpenAccount = accountType === 'open';
    // While the user's Amazon catalogue is syncing, closed accounts get
    // temporary Rainforest access (same path as open accounts).
    const useRainforest = isOpenAccount || isBridgeActive;

    try {
      const token = authCtx?.token;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Open accounts (or closed accounts in bridge mode): hit Rainforest.
      // Closed accounts post-sync: catalogue lookup (own products only).
      const endpoint = useRainforest
        ? `${API_BASE_URL}/rainforest/lookup-asin`
        : `${API_BASE_URL}/amazon/catalogue/lookup`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          asin: asinToFetch,
          amazon_domain: 'amazon.in',
          listingMode: 'existing'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || errorData.message || '';

        if (response.status === 404 ||
            errorMessage.toLowerCase().includes('not found')) {
          setShowAsinNotFoundModal(true);
          setError(null);
          setIsLoading(false);
          return;
        }

        throw new Error(errorData.error || `Error: ${response.status}`);
      }

      const data = await response.json();
      if (data && data.result) {
        setAsin(asinToFetch);
        if (onAsinChange) onAsinChange(asinToFetch);

        // Store product data for reference card
        setFetchedProductData({
          asin: asinToFetch,
          title: data.result.title || data.result.Title || data.result.existing_title || '',
          firstImage: data.result.main_image || (data.result.all_images && data.result.all_images[0]) || '',
          isParent: data.is_parent || false,
          childrenCount: data.children_count || 0
        });

        // Pass result to parent (same format as Rainforest)
        // Include children data if parent ASIN
        onDataFetched(data.result, asinToFetch, data.children || [], data.is_parent || false);

        if (authCtx.fetchUserData && authCtx.token) {
          try {
            await authCtx.fetchUserData(authCtx.token);
          } catch (error) {}
        }
      } else {
        throw new Error('No product data received');
      }
    } catch (err) {
      setError(err.message || "Failed to fetch product data. Please try again.");
      if (onError) onError(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle catalogue modal "Select" button
  const handleCatalogueSelect = () => {
    if (selectedAsins.length === 0) return;
    setShowCatalogueModal(false);
    // Use the first selected ASIN (single listing = single product)
    handleFetchFromCatalogue(selectedAsins[0]);
  };

  const handleRemoveReference = () => {
    setFetchedProductData(null);
    setAsin('');
    setError(null);
    if (onAsinChange) onAsinChange('');
  };

  return (
    <div className={styles.asinLookupFormContainer}>
      {error && <p className={styles.asinLookupError}>{error}</p>}

      <div className={styles.asinLookupInputContainer}>
        <div className={styles.asinLookupInputCard}>
          <div className={styles.asinLookupInputWrapper}>
            <input
              id="asinInput"
              type="text"
              className={styles.asinLookupInput}
              placeholder="Enter ASIN to fetch Product Listing"
              value={asin}
              onChange={(e) => {
                const newValue = e.target.value;
                setAsin(newValue);
                setError(null);
                if (onAsinChange) onAsinChange(newValue);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!isLoading && !disabled && asin.trim() && asin.trim().length >= 10) {
                    handleFetchFromCatalogue(asin.trim());
                  }
                }
              }}
              disabled={isLoading || disabled}
            />
            {fetchedProductData ? (
              <div
                role="button"
                tabIndex={0}
                className={styles.asinLookupAddButton}
                onClick={handleRemoveReference}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRemoveReference(); } }}
                style={{ cursor: 'pointer' }}
                title="Clear selection"
                aria-label="Clear selection"
              >
                <FaTimes style={{ color: '#78B2F3', fontSize: 16 }} />
              </div>
            ) : (
              <div
                role="button"
                tabIndex={(!asin || !asin.trim()) ? -1 : 0}
                className={styles.asinLookupAddButton}
                onClick={(e) => {
                  e.preventDefault();
                  const trimmedAsin = asin.trim();
                  if (trimmedAsin) {
                    window.open(`https://amazon.in/dp/${trimmedAsin}`, '_blank');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const trimmedAsin = asin.trim();
                    if (trimmedAsin) window.open(`https://amazon.in/dp/${trimmedAsin}`, '_blank');
                  }
                }}
                aria-label="Open on Amazon"
                aria-disabled={!asin || !asin.trim()}
                style={{
                  cursor: (!asin || !asin.trim()) ? 'not-allowed' : 'pointer',
                  opacity: (!asin || !asin.trim()) ? 0.5 : 1
                }}
              >
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="32" height="32" rx="8" fill="#78B2F3"/>
                  <path d="M22.6012 8.00004H19.4022C18.9735 8.00004 18.6246 8.34894 18.6246 8.77769C18.6246 9.20644 18.9735 9.55534 19.4022 9.55534H21.342L15.3457 15.5517C15.041 15.8563 15.041 16.3467 15.3457 16.6513C15.4967 16.8023 15.6963 16.8804 15.8942 16.8804C16.0921 16.8804 16.2917 16.8049 16.4427 16.6513L22.4391 10.655V12.3882C22.4391 12.817 22.788 13.1658 23.2167 13.1658C23.6455 13.1658 23.9944 12.817 23.9944 12.3882V9.39559C23.9944 8.62663 23.3677 8 22.5988 8L22.6012 8.00004Z" fill="white"/>
                  <path d="M23.2197 14.808C22.7909 14.808 22.442 15.1569 22.442 15.5856V20.0822C22.442 21.3823 21.3841 22.442 20.0822 22.442H11.9151C10.615 22.442 9.5553 21.3841 9.5553 20.0822V11.9151C9.5553 10.615 10.6133 9.5553 11.9151 9.5553H16.4117C16.8405 9.5553 17.1894 9.2064 17.1894 8.77765C17.1894 8.3489 16.8405 8 16.4117 8H11.9151C9.75751 8 8 9.75491 8 11.9151V20.0849C8 22.2425 9.75491 24 11.9151 24H20.0849C22.2425 24 24 22.2451 24 20.0849V15.5883C24 15.1595 23.6511 14.8106 23.2224 14.8106L23.2197 14.808Z" fill="white"/>
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>

      <AsinNotFoundModal
        isOpen={showAsinNotFoundModal}
        onClose={() => setShowAsinNotFoundModal(false)}
      />

      <SelectProductCatalogueModal
        show={showCatalogueModal}
        onClose={() => setShowCatalogueModal(false)}
        products={catalogueProducts}
        selectedAsins={selectedAsins}
        onAsinSelect={handleAsinSelect}
        onSelectAll={handleSelectAll}
        onAnalyze={handleCatalogueSelect}
        selectAll={selectAll}
        actionLabel="Select"
        singleSelect
      />
    </div>
  );
});

export default AsinLookupForm;
